import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ISuggestion, useSuggestions } from '../../context/SuggestionsContext';
import SuggestionCard from './SuggestionCard';
import { PbsIcon } from '../common/icons';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { NOTIFICATION_AREAS } from '../../constants/suggestions';

interface INavItem {
  name: string;
  icon: string;
  key: string;
  notificationArea: string;
  count: number;
  loaded: boolean;
}

export interface ISuggestionListingProps {
  items: ISuggestion[];
  RefreshData: () => void;
  showCategoriesOnListing: boolean;
  showDocuSignMessage: boolean;
  adminMode: boolean;
  readOnly?: boolean;
  totalCount?: number;
  categoryCounts?: Record<string, number>;
  activeCategory?: string;
  onCategoryChange?: (notificationArea: string) => void;
  loading?: boolean;
}

// Exact icon per known notification area (see NOTIFICATION_AREAS).
const CATEGORY_ICONS: Record<string, string> = {
  'HR': 'People',
  'Credentialing': 'CertifiedDatabase',
  'General': 'Info',
  'Authorization Management': 'ClipboardList',
  'Payroll': 'DollarSign',
  'Background Checks': 'UserCheck',
  'Calendar': 'Calendar',
  'Staffing': 'UserPlus',
  'Professional Development': 'Education',
  'Time Entry': 'Clock',
  'E-Sign': 'EditNote',
  'Health Insurance Details': 'HeartPulse',
  'Client Documentation': 'DocumentSet',
};

const getCategoryIcon = (category: string): string => {
  if (CATEGORY_ICONS[category]) return CATEGORY_ICONS[category];
  // Fallback for arbitrary / client-side-derived area names.
  const c = category.toLowerCase();
  if (c.includes('credential')) return 'CertifiedDatabase';
  if (c.includes('background')) return 'UserCheck';
  if (c.includes('e-sign') || c.includes('esign') || c.includes('sign')) return 'EditNote';
  if (c.includes('professional') || c.includes('development')) return 'Education';
  if (c.includes('hr') || c.includes('human resource')) return 'People';
  if (c.includes('payroll')) return 'DollarSign';
  if (c.includes('time')) return 'Clock';
  if (c.includes('calendar')) return 'Calendar';
  if (c.includes('staff')) return 'UserPlus';
  if (c.includes('authorization')) return 'ClipboardList';
  if (c.includes('health') || c.includes('insurance')) return 'HeartPulse';
  if (c.includes('client') || c.includes('document')) return 'DocumentSet';
  if (c.includes('general')) return 'Info';
  return 'Mail';
};

const SuggestionListing: React.FC<ISuggestionListingProps> = ({
  items,
  RefreshData,
  showCategoriesOnListing,
  showDocuSignMessage,
  adminMode,
  readOnly = false,
  totalCount,
  categoryCounts,
  activeCategory,
  onCategoryChange,
  loading = false,
}) => {
  const [currentItems, setCurrentItems] = useState<ISuggestion[]>([]);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>('key0');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { suggestions } = useSuggestions();
  // In server-side mode, only use passed items — don't fall back to stale context suggestions
  const allItems = useMemo(
    () => (onCategoryChange ? items : (items.length > 0 ? items : suggestions)),
    [items, suggestions, onCategoryChange]
  );

  const notificationAreas = useMemo((): INavItem[] => {
    // Server-side mode: always show all 13 hardcoded categories.
    // counts arrive progressively; undefined = still loading (disabled), 0 = disabled, >0 = enabled
    if (onCategoryChange) {
      const areas = NOTIFICATION_AREAS.map((title, index): INavItem => {
        const count = categoryCounts?.[title]; // undefined = not yet fetched
        return {
          name: count !== undefined ? `${title} (${count})` : title,
          icon: getCategoryIcon(title),
          key: `key${index + 1}`,
          notificationArea: title,
          count: count ?? 0,
          loaded: count !== undefined,
        };
      });
      const allCount = totalCount ?? allItems.length;
      areas.unshift({
        name: `All (${allCount})`,
        icon: 'InboxCheck',
        key: 'key0',
        notificationArea: 'All',
        count: allCount,
        loaded: true,
      });
      return areas;
    }

    // Client-side mode: derive categories from the items we have
    if (allItems.length === 0) return [];

    const areaMap = new Map<string, number>();
    allItems.forEach((item) => {
      const area = item.NotificationArea ?? 'Other';
      areaMap.set(area, (areaMap.get(area) ?? 0) + 1);
    });

    const areas = Array.from(areaMap.entries()).map(([name, count], index): INavItem => ({
      name: `${name} (${count})`,
      icon: getCategoryIcon(name),
      key: `key${index + 1}`,
      notificationArea: name,
      count,
      loaded: true,
    }));

    areas.unshift({
      name: `All (${totalCount ?? allItems.length})`,
      icon: 'InboxCheck',
      key: 'key0',
      notificationArea: 'All',
      count: totalCount ?? allItems.length,
      loaded: true,
    });

    return areas.filter(area => area.count > 0);
  }, [allItems, totalCount, categoryCounts, onCategoryChange]);

  // Sync highlighted category when the parent changes activeCategory (server-side mode)
  useEffect(() => {
    if (!activeCategory || notificationAreas.length === 0) return;
    const match = notificationAreas.find(a => a.notificationArea === activeCategory);
    if (match) setSelectedCategoryKey(match.key);
  }, [activeCategory, notificationAreas]);

  // Keep currentItems in sync with allItems for client-side filtering
  useEffect(() => {
    setCurrentItems(allItems);
  }, [allItems]);

  const handleMenuLinkClick = useCallback((item: INavItem) => {
    if (onCategoryChange && item.key !== 'key0' && (!item.loaded || item.count === 0)) return;
    setSelectedCategoryKey(item.key);
    if (onCategoryChange) {
      onCategoryChange(item.notificationArea);
    } else {
      const filtered = item.notificationArea === 'All'
        ? allItems
        : allItems.filter(s => (s.NotificationArea ?? 'Other') === item.notificationArea);
      setCurrentItems(filtered);
    }
  }, [allItems, onCategoryChange]);

  // In server-side mode the parent owns what's displayed; in client-side mode use local filtered state
  const displayItems = onCategoryChange ? allItems : currentItems;

  if (displayItems.length === 0 && !loading && !onCategoryChange) {
    return (
      <div className="p-5 text-center text-muted-foreground">
        No notifications available.
      </div>
    );
  }

  return (
    <div className="border-t-2 border-border flex flex-row gap-[10px]">
      {showCategoriesOnListing && (
        <div className={isMobile ? 'p-0' : 'flex-[0_0_25%] p-0'}>
          {isMobile ? (
            <div className="flex flex-col justify-between">
              {notificationAreas.map((area) => {
                const isSelected = selectedCategoryKey === area.key;
                const isDisabled = area.key !== 'key0' && (!area.loaded || area.count === 0);
                return (
                  <Tooltip key={area.key}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isSelected ? 'default' : 'ghost'}
                        size="icon"
                        disabled={isDisabled}
                        onClick={() => handleMenuLinkClick(area)}
                        className="w-10 h-10 mb-4 p-0"
                        title={area.name}
                      >
                        <PbsIcon iconName={area.icon} size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{area.name}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <ul className="list-none p-0 m-0">
              {notificationAreas.map((item) => {
                const isSelected = selectedCategoryKey === item.key;
                const isDisabled = item.key !== 'key0' && (!item.loaded || item.count === 0);
                return (
                  <li key={item.key} className="mb-1">
                    <Button
                      variant={isSelected ? 'default' : 'ghost'}
                      disabled={isDisabled}
                      onClick={() => handleMenuLinkClick(item)}
                      className={cn(
                        'w-full justify-start px-3 py-2 h-auto min-h-8',
                        isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                        isDisabled && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <PbsIcon iconName={item.icon} size={16} className="mr-2 flex-shrink-0" />
                      <span className="flex-1 text-left">{item.name}</span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className={isMobile ? 'w-full' : 'flex-1 -ml-[10px]'}>
        {loading ? (
          <div className="flex justify-center items-center p-5">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2" />
            <span className="text-sm text-muted-foreground">Loading Notifications...</span>
          </div>
        ) : (
          displayItems.map((suggestion) => (
            <SuggestionCard
              key={suggestion.Id}
              suggestion={suggestion}
              size="full"
              refreshParent={RefreshData}
              showDocuSignMessage={showDocuSignMessage}
              adminMode={adminMode}
              isViewMoreMobile={isMobile}
              readOnly={readOnly}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default SuggestionListing;
